import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../config/firebase';
import { useOnboardingStore } from '../../onboarding/store/onboardingStore';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { useAuthStore } from '../store/authStore';

// Map Firebase error codes to user-friendly messages
const getErrorMessage = (error: any) => {
  // If it's an axios error with a response from our backend
  if (error.response?.data?.error) {
    // Check for email verification errors
    if (error.response.data.requiresVerification) {
      return 'Email verification required. Please verify your email before signing in.';
    }
    if (error.response.data.error?.message) {
      return error.response.data.error?.message;
    }
  }

  // Firebase error codes
  const errorMessages = {
    'auth/invalid-credential': 'Invalid email or password. Please check your credentials.',
    'auth/user-not-found': 'No account exists with this email. Would you like to sign up?',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'An account already exists with this email. Would you like to sign in?',
    'auth/weak-password': 'Password should be at least 6 characters long.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/popup-closed-by-user': 'Sign-in window was closed. Please try again.',
    'auth/cancelled-popup-request': 'The process was cancelled. Please try again.',
    'auth/popup-blocked': 'Pop-up was blocked by the browser. Please allow pop-ups for this site.',
    'auth/network-request-failed': 'Network error. Please check your internet connection.',
    'auth/email-not-verified': 'Email verification required. Please verify your email before signing in.',
    'default': 'An error occurred during authentication. Please try again.'
  };

  return error.code ? errorMessages[error.code] || errorMessages.default : errorMessages.default;
};

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uid, setUid] = useState(null);

  /* useEffect(() => {
    const checkAndRefreshToken = async () => {
    const currentUser = auth.currentUser;

    if (currentUser && currentUser.emailVerified) {
      try {
      const token = await authService.getToken(true);
      if (token) {
        userService.setAuthToken(token);
        authService.startTokenRefresh();
      }
      } catch (error) {
      console.error('Error refreshing token on mount:', error);
      }
    } else if (currentUser && !currentUser.emailVerified) {
      console.log('User exists but email not verified. Logging out.');
      await authService.handleLogout();
    }
    };

    checkAndRefreshToken();

    return () => {
    authService.stopTokenRefresh();
    };
  }, []); */

  const navigate = useNavigate();
  const { setAuth, clearAuth } = useAuthStore();
  const { resetOnboarding } = useOnboardingStore();

  const handleAuthSuccess = async (token?: string) => {
    try {
      // Reset onboarding store on successful authentication
      resetOnboarding();

      // Get token if not provided
      if (!token) {
        token = await authService.getToken();
      }

      // Set token for API calls
      userService.setAuthToken(token);

      // Get user data from backend
      const userData = await userService.validateToken();

      // Update store with both token and user data
      setAuth(token, userData);


      if (!userData.onboarding?.completed) {
        navigate(`/onboarding-${userData.onboarding?.currentStep ?? 1}`);
        return;
      }

      // Start token refresh process
      authService.startTokenRefresh();

      // Navigate to home
      navigate('/');
    } catch (error) {
      console.error('Backend sync error:', error);
      throw error; // Re-throw to be handled by the caller
    }
  };

  const handleAuthError = (error: any) => {
    console.error('Auth Error:', error);
    try {
      setError(getErrorMessage(error));
    } catch (e) {
      console.error('Error getting error message:', e);
      setError('An error occurred during authentication. Please try again.');
    }
    setUid(error?.response?.data?.uid || null);
    setLoading(false);
    authService.stopTokenRefresh(); // Stop token refresh
    clearAuth(); // Clear auth state on error
    resetOnboarding(); // Reset onboarding state on error
  };

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      await authService.loginWithEmail(email, password);
      await handleAuthSuccess();
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string, role: string = '') => {
    try {
      setLoading(true);
      setError(null);

      // 1. Register with Firebase Auth
      const user = await authService.registerWithEmail(email, password);
      console.log('Firebase Auth registration successful:', user);

      // 2. Update Firebase profile
      await authService.updateProfile(name);
      console.log('Firebase profile updated');

      const invitationId = sessionStorage.getItem('invitationId');

      // 3. Register with backend without storing the token
      // We don't get or store the token during registration since email isn't verified yet
      const response = await userService.register({
        uid: user.uid,
        email: user.email,
        name,
        role,
        invitationId
      });
      console.log('Backend registration successful', response);

      // The invitation has been accepted at this point, so the stored copy is
      // spent: leaving it behind would pre-fill a later signup with a stale
      // address.
      sessionStorage.removeItem('invitationId');
      sessionStorage.removeItem('invitationEmail');

      // 6. Check if email verification is required
      if (response.requiresVerification) {
        // Logout from Firebase after registration if email verification is required
        // This prevents the user from being considered authenticated until they verify their email
        await authService.handleLogout(false);

        // Redirect to email verification page
        navigate(`/verify-email?uid=${user.uid}`);
        return;
      }
      console.log('Registration flow completed');
    } catch (error) {
      console.error('Registration error:', error);
      handleAuthError(error);

      // If Firebase registration succeeded but backend failed, try to clean up
      if (auth.currentUser) {
        try {
          await auth.currentUser.delete();
          console.log('Cleaned up Firebase user after backend registration failed');
        } catch (deleteError) {
          console.error('Error cleaning up Firebase user:', deleteError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.handleLogout();
      clearAuth();
      resetOnboarding();
      userService.setAuthToken(null);
      window.location.href = '/signin';
    } catch (error) {
      handleAuthError(error);
    }
  };

  const isAuthenticated = () => {
    // Get authentication state from Zustand store
    const { isAuthenticated: storeAuthenticated } = useAuthStore.getState();

    // Consider user authenticated if Firebase Auth has user OR store has authenticated state
    return !!auth.currentUser || storeAuthenticated;
  };

  const validateInvitation = async (invitationId: string) => {
    try {
      const response = await userService.validateInvitation(invitationId);
      return response;
    } catch (error) {
      console.error('Error validating invitation:', error);
      throw error;
    }
  };

  return {
    login,
    register,
    logout,
    loading,
    error,
    uid,
    isAuthenticated,
    clearError: () => setError(null),
    validateInvitation
  };
};
